import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import type { WorkflowBlock, PortSide } from '@/types/workflow';

interface CrossSheetSource {
  sheetIndex: number;
  blockId: string;
  port?: PortSide;
  blockName: string;
}

export interface UseCrossSheetLinkingDeps {
  blocks: WorkflowBlock[];
  activeSheetIndex: number;
  /** Materialise the adjacent-sheet connector in the multi-sheet state.
   * A-102: returnerar resultatkod (`'created'` eller specifik error). */
  createCrossSheetConnector: (
    fromSheetIndex: number,
    toSheetIndex: number,
    fromBlockId: string,
    toBlockId: string,
    fromPort?: PortSide,
    toPort?: PortSide,
  ) => 'created' | 'invalid-sheet-index' | 'non-adjacent' | 'dangling-from-block' | 'dangling-to-block' | 'port-in-use';
  /** Mutex against the other modes. */
  setConnectMode: (v: boolean) => void;
  setGroupMode: (v: boolean | ((prev: boolean) => boolean)) => void;
  setGroupSelection: (ids: string[] | ((prev: string[]) => string[])) => void;
  logAction: (action: string, detail?: string) => void;
  announce: (message: string) => void;
}

export interface UseCrossSheetLinkingResult {
  crossSheetMode: boolean;
  crossSheetSource: CrossSheetSource | null;
  handleToggleCrossSheetMode: () => void;
  handleCrossSheetPick: (blockId: string, port?: PortSide) => void;
  /** Pre-pick a block as source (used from the Properties panel). */
  startCrossSheetFromBlock: (blockId: string) => void;
}

/**
 * Cross-sheet linking state machine. Extracted from Index.tsx so the
 * two-click "source on sheet A, target on adjacent sheet B" flow can be
 * reasoned about — and eventually unit-tested — in isolation.
 *
 * Cross-sheet connectors are only valid between ADJACENT sheets
 * (`|fromSheetIndex - toSheetIndex| === 1`). The hook enforces that.
 *
 * Escape behaviour: while the mode is active, Esc clears the source
 * selection; pressing Esc again exits the mode entirely.
 */
export function useCrossSheetLinking(deps: UseCrossSheetLinkingDeps): UseCrossSheetLinkingResult {
  const {
    blocks, activeSheetIndex, createCrossSheetConnector,
    setConnectMode, setGroupMode, setGroupSelection,
    logAction, announce,
  } = deps;

  const [crossSheetMode, setCrossSheetMode] = useState(false);
  const [crossSheetSource, setCrossSheetSource] = useState<CrossSheetSource | null>(null);

  const handleToggleCrossSheetMode = useCallback(() => {
    setCrossSheetMode(prev => {
      const next = !prev;
      if (next) {
        setConnectMode(false);
        setGroupMode(false);
        setGroupSelection([]);
        setCrossSheetSource(null);
      } else {
        setCrossSheetSource(null);
      }
      return next;
    });
  }, [setConnectMode, setGroupMode, setGroupSelection]);

  // C8: crossSheetSource-ref:en behålls som skydd mot stale closures i
  // memo:ade canvas-children, men activeSheetIndex läses nu direkt från
  // closure:en (props). useEffect-propagation av ref:en hade ett race-
  // fönster där sheet-switch → klick kom före effect-commit → ref stale.
  // activeSheetIndex är i useCallback-deps nu, så vi får fresh värde
  // utan ref-mellansteg.
  const crossSheetSourceRef = useRef(crossSheetSource);
  useEffect(() => { crossSheetSourceRef.current = crossSheetSource; }, [crossSheetSource]);

  const handleCrossSheetPick = useCallback((blockId: string, port?: PortSide) => {
    const source = crossSheetSourceRef.current;
    const activeIdx = activeSheetIndex;
    if (!source) {
      const block = blocks.find(b => b.id === blockId);
      setCrossSheetSource({
        sheetIndex: activeIdx,
        blockId,
        port,
        blockName: block?.config.name || 'Block',
      });
      toast({
        title: 'Source block picked',
        description: 'Switch to an adjacent sheet and click the target block.',
      });
      return;
    }
    if (source.sheetIndex === activeIdx && source.blockId === blockId) {
      // Click the same block → cancel selection
      setCrossSheetSource(null);
      return;
    }
    if (source.sheetIndex === activeIdx) {
      toast({
        title: 'Same sheet',
        description: 'Switch to an adjacent sheet to pick the target.',
        variant: 'destructive',
      });
      return;
    }
    const delta = Math.abs(source.sheetIndex - activeIdx);
    if (delta !== 1) {
      toast({
        title: 'Only adjacent sheets',
        description: 'Cross-sheet links can only connect neighbouring sheets (±1).',
        variant: 'destructive',
      });
      return;
    }
    const result = createCrossSheetConnector(
      source.sheetIndex, activeIdx,
      source.blockId, blockId,
      source.port, port,
    );
    if (result !== 'created') {
      // A-102: pre-flight failure — rapportera till user istället för
      // att tyst skapa en dangling connector.
      const descByError: Record<Exclude<typeof result, 'created'>, string> = {
        'invalid-sheet-index': 'Ogiltigt sheet-index.',
        'non-adjacent': 'Bara grannsheets kan länkas (±1).',
        'dangling-from-block': 'Käll-blocket finns inte längre.',
        'dangling-to-block': 'Målblocket finns inte längre.',
        'port-in-use': 'Porten används redan mellan dessa block — välj en annan port.',
      };
      toast({
        title: 'Cross-sheet link skapades inte',
        description: descByError[result],
        variant: 'destructive',
      });
      setCrossSheetSource(null);
      return;
    }
    const targetBlock = blocks.find(b => b.id === blockId);
    const desc = `${source.blockName} → ${targetBlock?.config.name || 'Block'}`;
    toast({ title: 'Cross-sheet link created', description: desc });
    logAction('Cross-sheet link created', desc);
    announce(`Cross-sheet link created: ${desc}`);
    setCrossSheetSource(null);
  }, [blocks, activeSheetIndex, createCrossSheetConnector, logAction, announce]);

  const startCrossSheetFromBlock = useCallback((blockId: string) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    setConnectMode(false);
    setGroupMode(false);
    setGroupSelection([]);
    setCrossSheetMode(true);
    setCrossSheetSource({
      sheetIndex: activeSheetIndex,
      blockId,
      blockName: block.config.name || 'Block',
    });
    toast({
      title: 'Cross-sheet link started',
      description: 'Switch to an adjacent sheet and click the target block.',
    });
  }, [activeSheetIndex, blocks, setConnectMode, setGroupMode, setGroupSelection]);

  // Esc while in cross-sheet mode: first press clears source, second exits mode.
  useEffect(() => {
    if (!crossSheetMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (crossSheetSourceRef.current) {
        setCrossSheetSource(null);
      } else {
        setCrossSheetMode(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [crossSheetMode]);

  return {
    crossSheetMode,
    crossSheetSource,
    handleToggleCrossSheetMode,
    handleCrossSheetPick,
    startCrossSheetFromBlock,
  };
}
